import { Box, List, Stack } from "@mui/material";
import { SelectableListItem } from "@src/appV2/redesign/components/SelectableListItem/SelectableListItem";
import { Text } from "@src/appV2/redesign/components/Text";
import { type TimeSlotWithExtras } from "@src/appV2/Shifts/types";

import { APPLY_TO_OPTIONS, type ApplyToOption } from "./types";

interface ApplyToOptionsProps {
  selectedOption: ApplyToOption;
  onOptionChange: (option: ApplyToOption) => void;
  showAllShiftsOption?: boolean;
  timeSlot: TimeSlotWithExtras;
  qualificationName?: string;
}

interface OptionConfig {
  value: ApplyToOption;
  label: string;
  description: string;
}

function getTimeSlotLabel(timeSlot: TimeSlotWithExtras): string {
  return timeSlot.toUpperCase();
}

function getOptions(timeSlot: TimeSlotWithExtras, qualificationName?: string): OptionConfig[] {
  const slotLabel = getTimeSlotLabel(timeSlot);
  const qualificationPrefix = qualificationName ? `${qualificationName} ` : "";

  return [
    {
      value: APPLY_TO_OPTIONS.THIS_SHIFT_ONLY,
      label: "Just to this shift",
      description: "Set these times just for this shift",
    },
    {
      value: APPLY_TO_OPTIONS.THIS_AND_FOLLOWING,
      label: `Only to ${qualificationPrefix}${slotLabel} shifts`,
      description: `Will only be applied to ${qualificationPrefix}${slotLabel} shifts`,
    },
    {
      value: APPLY_TO_OPTIONS.ALL_SHIFTS,
      label: `Apply to all ${slotLabel} shifts`,
      description: `Set these times as default for all ${slotLabel} shifts`,
    },
  ];
}

export function ApplyToOptions(props: ApplyToOptionsProps) {
  const {
    selectedOption,
    onOptionChange,
    showAllShiftsOption = true,
    timeSlot,
    qualificationName,
  } = props;

  const isOrientation = timeSlot === "ori";

  const options = getOptions(timeSlot, qualificationName);
  const visibleOptions = options.filter((option) => {
    if (!showAllShiftsOption && option.value === APPLY_TO_OPTIONS.ALL_SHIFTS) {
      return false;
    }
    if (isOrientation && option.value === APPLY_TO_OPTIONS.THIS_AND_FOLLOWING) {
      return false;
    }
    return true;
  });

  return (
    <Box
      sx={(theme) => ({
        border: `1px solid ${theme.palette.border?.base}`,
        borderRadius: theme.borderRadius?.xLarge,
        overflow: "hidden",
      })}
    >
      <Box
        sx={(theme) => ({
          borderBottom: `1px solid ${theme.palette.border?.base}`,
          padding: theme.spacing(4),
          display: "flex",
          justifyContent: "center",
        })}
      >
        <Text color="text.secondary" variant="caption">
          How do you want to apply this time change?
        </Text>
      </Box>

      <List disablePadding>
        {visibleOptions.map((option, index) => (
          <Box
            key={option.value}
            sx={(theme) => ({
              borderBottom:
                index < visibleOptions.length - 1
                  ? `1px solid ${theme.palette.border?.base}`
                  : "none",
            })}
          >
            <SelectableListItem
              isSelected={selectedOption === option.value}
              selectionMode="radio"
              onSelect={() => {
                onOptionChange(option.value);
              }}
            >
              <Stack spacing={0.5}>
                <Text semibold>{option.label}</Text>
                <Text color="text.secondary" variant="caption">
                  {option.description}
                </Text>
              </Stack>
            </SelectableListItem>
          </Box>
        ))}
      </List>
    </Box>
  );
}
